import type {Assistant, AssistantChannel, AssistantModel, AssistantTask} from "@/types/ai";
import {DEFAULT_ASSISTANT_MODEL} from "@/types/ai";
import {getModelProvider} from "@/utils/ai/assistants";
import {isEuropeRegion} from "@/utils/common/region";

type AssistantWithTasks = Omit<Assistant, "id" | "lastModifiedAt" | "createdAt"> | Assistant;
type RegionBlockedAssistantToolType = "code_execution";

const EUROPE_BLOCKED_ASSISTANT_TOOLS = new Set<RegionBlockedAssistantToolType>([
   "code_execution",
]);

export function canUseAssistantCodeExecution(
   region: string | undefined = process.env.NEXT_PUBLIC_REGION
): boolean {
   return !isEuropeRegion(region);
}

export function canUseAnthropicModels(
   region: string | undefined = process.env.NEXT_PUBLIC_REGION
): boolean {
   return !isEuropeRegion(region);
}

/** Matches allowlisted Claude models and any future/legacy `claude-` identifiers. */
export function isAnthropicAssistantModel(model: string): boolean {
   return getModelProvider(model) === "anthropic" || model.startsWith("claude-");
}

export function sanitizeAssistantModelForRegion(
   model: string,
   region: string | undefined = process.env.NEXT_PUBLIC_REGION
): AssistantModel {
   if (!isEuropeRegion(region) || !isAnthropicAssistantModel(model)) {
      return model as AssistantModel;
   }
   return DEFAULT_ASSISTANT_MODEL;
}

export function sanitizeAssistantChannelsForRegion(
   channels: AssistantChannel[],
   region: string | undefined = process.env.NEXT_PUBLIC_REGION
): AssistantChannel[] {
   if (!isEuropeRegion(region)) {
      return channels;
   }

   return channels.map((channel) => ({
      ...channel,
      model: sanitizeAssistantModelForRegion(channel.model, region),
   }));
}

export function filterAssistantToolsForRegion(
   tools: AssistantTask["tools"],
   region: string | undefined = process.env.NEXT_PUBLIC_REGION
): AssistantTask["tools"] {
   if (!isEuropeRegion(region)) {
      return tools;
   }

   return tools.filter(
      (tool) => !EUROPE_BLOCKED_ASSISTANT_TOOLS.has(tool.type as RegionBlockedAssistantToolType)
   );
}

/**
 * EU persistence guard for assistant payloads: strips restricted task tools
 * (code_execution) and replaces blocked channel models (Anthropic).
 */
export function sanitizeAssistantForRegion<T extends AssistantWithTasks>(
   assistant: T,
   region: string | undefined = process.env.NEXT_PUBLIC_REGION
): T {
   if (!isEuropeRegion(region)) {
      return assistant;
   }

   return {
      ...assistant,
      tasks: assistant.tasks.map((task) => ({
         ...task,
         tools: filterAssistantToolsForRegion(task.tools, region),
      })),
      channels: sanitizeAssistantChannelsForRegion(assistant.channels, region),
   };
}
